import { store } from '../store';
import { DEBUG_LOGS } from '../utils/debugFlags';
import {
  setConnected,
  setDisconnected,
  updateCountdown,
  incrementReconnectAttempts,
  resetReconnectAttempts,
  setReconnecting,
  addPendingData
} from '../store/connectionSlice';
import { clearAuth } from '../store/authSlice';
import apiService, { API_BASE_URL } from './api';

class ConnectionMonitor {
  private checkInterval: NodeJS.Timer | null = null;
  private countdownInterval: NodeJS.Timer | null = null;
  private reconnectTimeout: NodeJS.Timer | null = null;
  private isMonitoring: boolean = false;
  private readonly CHECK_INTERVAL = 5000; // Check every 5 seconds
  private readonly MAX_RECONNECT_ATTEMPTS = 12; // 12 attempts = 60 seconds
  private readonly COUNTDOWN_DURATION = 60; // 60 second countdown
  
  // Track consecutive failures
  private consecutiveFailures = 0;
  private readonly FAILURE_THRESHOLD = 3; // Consider disconnected after 3 failures (reduces spurious logouts on flaky networks)

  start() {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    this.consecutiveFailures = 0;
    
    // Start periodic health checks
    this.checkInterval = setInterval(() => {
      this.checkConnection();
    }, this.CHECK_INTERVAL);
    
    // Initial check
    this.checkConnection();
  }

  stop() {
    this.isMonitoring = false;
    
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private async checkConnection() {
    const state = store.getState();
    
    // Skip check if user is not authenticated or doesn't have a valid token
    if (!state.auth.isAuthenticated || !state.auth.token || !state.auth.user) {
      if (DEBUG_LOGS) {
        console.log('[CONNECTION DEBUG] Skipping health check - user not fully authenticated');
      }
      return;
    }

    let timeoutId: NodeJS.Timeout | undefined;
    try {
      // Make a lightweight health check request with authentication
      // Create browser-compatible timeout signal (10s to avoid production latency false positives)
      const abortController = new AbortController();
      const HEALTH_CHECK_TIMEOUT_MS = 10000;
      timeoutId = setTimeout(() => {
        abortController.abort();
      }, HEALTH_CHECK_TIMEOUT_MS);

      const response = await fetch(`${API_BASE_URL}/health/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${state.auth.token}`,
          'Content-Type': 'application/json',
        },
        signal: abortController.signal
      });

      // Clear timeout since request completed
      clearTimeout(timeoutId);

      if (response.ok) {
        // Connection is good
        this.handleConnectionRestored();
      } else if (response.status === 401) {
        // Authentication failed - don't treat as connection loss
        // Let the normal auth failure handler deal with this
        this.consecutiveFailures = 0;
      } else {
        // Other HTTP errors - treat as connection issue
        this.handleConnectionFailure('Server error');
      }
    } catch (error: any) {
      // Clear timeout in case of error
      if (typeof timeoutId !== 'undefined') {
        clearTimeout(timeoutId);
      }
      
      // Network error - connection lost
      this.handleConnectionFailure(error.message || 'Network error');
    }
  }

  private handleConnectionFailure(error: string) {
    this.consecutiveFailures++;
    
    if (this.consecutiveFailures >= this.FAILURE_THRESHOLD) {
      const state = store.getState();
      
      if (!state.connection.isConnected) {
        // Already disconnected, just increment reconnect attempts
        if (state.connection.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
          store.dispatch(incrementReconnectAttempts());
        }
        return;
      }
      
      // Mark as disconnected and start countdown
      store.dispatch(setDisconnected({ error }));
      this.startCountdown();
      this.startReconnectionAttempts();
    }
  }

  private handleConnectionRestored() {
    const state = store.getState();
    
    // Reset failure counter
    this.consecutiveFailures = 0;
    
    if (!state.connection.isConnected) {
      // Connection restored!
      store.dispatch(setConnected(true));
      
      // Stop countdown
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      
      // Sync any pending data
      this.syncPendingData();
      
      // Show success notification
      this.showNotification('Connection restored', 'success');
    }
  }

  private startCountdown() {
    let seconds = this.COUNTDOWN_DURATION;
    
    // Clear any existing countdown
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    store.dispatch(updateCountdown(seconds));
    
    this.countdownInterval = setInterval(() => {
      seconds--;
      store.dispatch(updateCountdown(seconds));
      
      if (seconds <= 0) {
        // Time's up - save data and logout
        void this.handleForceLogout();
        
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
      }
    }, 1000);
  }

  private startReconnectionAttempts() {
    const state = store.getState();
    
    if (state.connection.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
      return; // Stop trying after max attempts
    }
    
    store.dispatch(setReconnecting(true));
    
    // Exponential backoff: 1s, 2s, 4s, 8s... max 10s
    const delay = Math.min(1000 * Math.pow(2, state.connection.reconnectAttempts), 10000);
    
    this.reconnectTimeout = setTimeout(() => {
      this.checkConnection();
      
      // Schedule next attempt if still disconnected
      const newState = store.getState();
      if (!newState.connection.isConnected) {
        this.startReconnectionAttempts();
      }
    }, delay);
  }

  private async confirmSessionStillValid(): Promise<boolean> {
    const state = store.getState();
    if (!state.auth.isAuthenticated || !state.auth.token || !state.auth.user) {
      return false;
    }

    let timeoutId: NodeJS.Timeout | undefined;
    try {
      const abortController = new AbortController();
      timeoutId = setTimeout(() => abortController.abort(), 5000);

      const response = await fetch(`${API_BASE_URL}/auth/session-status/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${state.auth.token}`,
          'Content-Type': 'application/json',
        },
        signal: abortController.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        return false;
      }

      const payload = await response.json().catch(() => ({}));
      return Boolean(payload && payload.authenticated === true);
    } catch {
      return false;
    } finally {
      if (typeof timeoutId !== 'undefined') {
        clearTimeout(timeoutId);
      }
    }
  }

  private async handleForceLogout() {
    const state = store.getState();

    // Before logging the user out, verify the session is actually invalid.
    // The previous behavior force-logged-out on any 60s+ network outage, which
    // includes routine cases like backend container rebuild or a heavy backend
    // operation (data-analysis overview on a 900K-row dataset can block a
    // single-worker Daphne for tens of seconds). Logging the user out in
    // those cases is destructive — it loses unsaved analysis state and forces
    // a re-login for what was actually just a transient blip.
    //
    // New rule: only log out if /auth/session-status/ explicitly says
    // authenticated:false. If the status endpoint itself can't be reached
    // (backend still down) we keep the banner up and keep retrying. The
    // user stays logged in until the backend either says "you're invalid"
    // or comes back healthy.
    const sessionValid = await this.confirmSessionStillValid();
    if (sessionValid) {
      console.warn(
        '[LOGOUT-DEBUG] connectionMonitor.handleForceLogout aborted | ' +
        'session-status confirms session is still valid; keeping user signed in. ' +
        'Restarting countdown and continuing reconnect attempts.'
      );
      // Reset reconnect counter so startReconnectionAttempts will schedule
      // new attempts instead of bailing on MAX_RECONNECT_ATTEMPTS.
      store.dispatch(resetReconnectAttempts());
      this.startCountdown();
      this.startReconnectionAttempts();
      return;
    }

    // confirmSessionStillValid() returns false on either (a) an explicit
    // authenticated:false response or (b) a network/timeout error. We only
    // want to force-logout for case (a). Probe one more time with a different
    // signature: if /auth/session-status/ is reachable AND returns 401, we know
    // the session is genuinely revoked. Otherwise treat as transient.
    let revokedExplicitly = false;
    try {
      const abortController = new AbortController();
      const probeTimeout = setTimeout(() => abortController.abort(), 5000);
      const probe = await fetch(`${API_BASE_URL}/auth/session-status/`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        signal: abortController.signal,
      });
      clearTimeout(probeTimeout);
      // 401 = explicitly revoked; any other reachable status with authenticated:false also counts
      if (probe.status === 401) {
        revokedExplicitly = true;
      } else if (probe.ok) {
        const payload = await probe.json().catch(() => ({}));
        if (payload && payload.authenticated === false) {
          revokedExplicitly = true;
        }
      }
    } catch {
      // Probe itself unreachable → backend is down. Don't force logout.
      revokedExplicitly = false;
    }

    if (!revokedExplicitly) {
      console.warn(
        '[LOGOUT-DEBUG] connectionMonitor.handleForceLogout aborted | ' +
        'cannot confirm session is revoked (backend likely unreachable). ' +
        'Keeping user signed in; banner stays up and reconnect attempts continue.'
      );
      this.startCountdown();
      this.startReconnectionAttempts();
      return;
    }

    console.warn(
      `[LOGOUT-DEBUG] connectionMonitor.handleForceLogout | reason=session_explicitly_revoked | ` +
      `countdown=${state.connection.countdownSeconds} | ` +
      `reconnectAttempts=${state.connection.reconnectAttempts} | ` +
      `isConnected=${state.connection.isConnected} | ` +
      `consecutiveFailures=${this.consecutiveFailures} | ` +
      `user=${state.auth.user?.username ?? 'null'} | ` +
      `userId=${state.auth.user?.id ?? 'null'} | ` +
      `wasAuthenticated=${state.auth.isAuthenticated} | ` +
      `timestamp=${new Date().toISOString()}`
    );

    // Save any unsaved data to localStorage (drafts are already auto-saved)
    this.saveDataToLocalStorage();

    // Clear auth and redirect to login
    store.dispatch(clearAuth());

    this.showNotification(
      'Your session was ended by the server. Please sign in again.',
      'error'
    );

    this.stop();

    setTimeout(() => {
      const currentPath = window.location.pathname;
      window.location.href = currentPath.startsWith('/onecare') ? '/onecare/login' : '/login';
    }, 2000);
  }

  private saveDataToLocalStorage() {
    const state = store.getState();

    // Save any pending data
    const dataToSave = {
      pendingMessages: [], // Messages are handled by WebSocket separately
      pendingFeedback: state.patientFeedback || {},
      pendingNotifications: state.connection.pendingData || [],
      savedAt: new Date().toISOString(),
      userId: state.auth.user?.id
    };

    localStorage.setItem('hmcs_pending_data', JSON.stringify(dataToSave));

    // Count auto-saved drafts (managed independently by useDraftAutosave hook)
    const userId = state.auth.user?.id;
    if (userId) {
      const prefix = `draft_${userId}_`;
      let draftCount = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) draftCount++;
      }
      if (DEBUG_LOGS) {
        console.log(`[ConnectionMonitor] Data saved to localStorage. ${draftCount} draft(s) already persisted.`);
      }
    } else if (DEBUG_LOGS) {
      console.log('[ConnectionMonitor] Data saved to localStorage:', dataToSave);
    }
  }

  private async syncPendingData() {
    try {
      const savedData = localStorage.getItem('hmcs_pending_data');
      
      if (!savedData) return;
      
      const data = JSON.parse(savedData);
      const state = store.getState();
      
      // Only sync if it's the same user
      if (data.userId !== state.auth.user?.id) {
        localStorage.removeItem('hmcs_pending_data');
        return;
      }
      
      // TODO: Implement actual sync logic for each data type
      if (DEBUG_LOGS) {
        console.log('[ConnectionMonitor] Syncing pending data:', data);
      }
      
      // Clear after successful sync
      localStorage.removeItem('hmcs_pending_data');
      
      this.showNotification('Pending data synchronized successfully', 'success');
    } catch (error) {
      console.error('[ConnectionMonitor] Failed to sync pending data:', error);
    }
  }

  private showNotification(message: string, type: 'success' | 'error' | 'warning' = 'warning') {
    // Dispatch a global notification
    if (window.store && window.store.dispatch) {
      window.store.dispatch({
        type: 'globalNotifications/showNotificationAlert',
        payload: {
          id: `connection-${Date.now()}`,
          type: 'system',
          title: 'Connection Status',
          message,
          timestamp: Date.now(),
          urgencyLevel: type === 'error' ? 'critical' : 'routine'
        }
      });
    }
  }

  // Method to save data before any risky operation
  saveCurrentWork(data: any) {
    store.dispatch(addPendingData(data));
    this.saveDataToLocalStorage();
  }
}

// Create singleton instance
const connectionMonitor = new ConnectionMonitor();

export default connectionMonitor;